import { useReactiveVar } from '@apollo/client'
import React from 'react'

import { LoyaltyCardStamp } from '@src/components/SingleOutlet/types'
import {
  DetailedPaymentMethod,
  Discount,
  LoyaltyCard as LoyaltyCardType,
} from '@src/graphql-types'
import {
  LoyaltyListChildName,
  MainRouteName,
} from '@src/hooks/useAccountRouter'
import { useFormatCurrency } from '@src/hooks/useFormatCurrency'
import { UseBasketTotalsResponseData } from '@src/hooks/useTotals/useBasketTotals'
import { paymentMethodVar } from '@src/hooks/useTotals/utils/paymentMethodReactiveVar'
import { imageJitURL } from '@src/utils/imageJitURL'

import {
  LoyaltyCardLinkContainer,
  LoyaltyCardImage,
  LoyaltyCardIconAndTextContainer,
  DescriptionAndStampsContainer,
  LoyaltyCardInnerContainer,
  LoyaltyCardTitle,
  OuterContainer,
  TitleAndDescriptionContainer,
} from './CustomerLoyaltyCard.styles'
import { LoyaltyCardInfoText } from './LoyaltyCardInfoText'
import { LoyaltyCardStampProgressMeter } from './StampProgressMeter'
import { ScallopedMaskSVGLarge } from '../SVGS/ScallopedMaskSVGLarge'
import { kebabCase } from 'lodash'

export type CustomerLoyaltyCard = Pick<
  LoyaltyCardType,
  | 'id'
  | 'requiredStamps'
  | 'discountStrategy'
  | 'allowCashOrders'
  | 'isAvailableToday'
  | 'loyaltyCardColor'
  | 'loyaltyCardIcon'
  | 'mainImage'
  | 'nameOverride'
  | 'netTotal'
> & {
  amountToSpendToEarnAStamp?: LoyaltyCardType['amountToSpendToEarnAStamp']
  accountsSectionTextOverride?: LoyaltyCardType['accountsSectionTextOverride']
  discount: Pick<
    Discount,
    | 'id'
    | 'minimumSubtotalGross'
    | 'name'
    | 'discountAmount'
    | 'discountPercentage'
  >
} & { loyaltyCardStamps: LoyaltyCardStamp[] }

export const CustomerLoyaltyCard: React.FC<{
  loyaltyCard: CustomerLoyaltyCard | null
  page?: 'terms' | 'burger-menu-list'
  basketTotals?: UseBasketTotalsResponseData
  isFinalCard?: boolean
  singleBasketCard?: boolean
  isAccountsSection?: boolean
}> = ({
  loyaltyCard,
  page,
  basketTotals,
  singleBasketCard = false,
  isFinalCard = false,
  isAccountsSection = false,
}) => {
  const formatCurrency = useFormatCurrency()
  const paymentMethod = useReactiveVar(paymentMethodVar)
  if (!loyaltyCard) {
    return null
  }

  const isTermsPage = page === 'terms'
  const isBurgerMenuList = page === 'burger-menu-list'

  const { discount } = loyaltyCard
  const loyaltyCardStamps = loyaltyCard.loyaltyCardStamps || []
  const amountToSpend =
    loyaltyCardStamps.length < loyaltyCard.requiredStamps
      ? loyaltyCard.amountToSpendToEarnAStamp ?? 0
      : 0

  const canStampBeEarned = loyaltyCard.isAvailableToday
    ? amountToSpend === 0 &&
      (loyaltyCard.allowCashOrders ||
        paymentMethod.detailedPaymentMethod !== DetailedPaymentMethod.CASH)
    : false

  // Use mainImage in accounts section, fallback to loyaltyCardIcon
  const useMainImage = Boolean(isAccountsSection && loyaltyCard.mainImage)

  return (
    <OuterContainer>
      <LoyaltyCardLinkContainer
        showAnimation={
          loyaltyCard.loyaltyCardStamps.length === loyaltyCard.requiredStamps
        }
        isFinalCard={isFinalCard}
        singleBasketCard={singleBasketCard}
        loyaltyCardColor={loyaltyCard.loyaltyCardColor}
        route={{
          ...(isBurgerMenuList
            ? {
                mainRouteName: MainRouteName.LOYALTY_LIST,
                childRouteName: LoyaltyListChildName.LOYALTY,
              }
            : { mainRouteName: MainRouteName.LOYALTY }),
          pageDataId: discount?.id,
        }}
        isBurgerMenuList={isBurgerMenuList}
        dataTestId="loyalty-card-link-container"
      >
        <LoyaltyCardInnerContainer>
          <LoyaltyCardIconAndTextContainer>
            <ScallopedMaskSVGLarge scale={0.6} />
            <LoyaltyCardImage
              useClipPath={!useMainImage}
              isTermsPage={false}
              role="img"
              aria-label={discount?.name}
              canStampBeEarned={canStampBeEarned}
              imageUrl={imageJitURL(
                useMainImage
                  ? loyaltyCard.mainImage
                  : loyaltyCard.loyaltyCardIcon,
                {
                  resize: {
                    width: 168,
                    height: 168,
                    fit: 'cover',
                  },
                }
              )}
              scale={0.6}
              data-test-id="loyalty-card-image-main"
            />
            <DescriptionAndStampsContainer>
              <TitleAndDescriptionContainer>
                <LoyaltyCardTitle
                  loyaltyCardColor={loyaltyCard.loyaltyCardColor}
                  data-test-id={`loyalty-card-text-${kebabCase(discount.name)}`}
                >
                  {(isAccountsSection && loyaltyCard.nameOverride) ||
                    discount.name}
                </LoyaltyCardTitle>
                <LoyaltyCardInfoText
                  loyaltyCard={loyaltyCard}
                  basketTotals={basketTotals}
                  isTermsPage={isTermsPage}
                  amountToSpendToEarnStamp={formatCurrency(amountToSpend)}
                  paymentMethod={paymentMethod.detailedPaymentMethod}
                  applyAccountsSectionTextOverride={isAccountsSection}
                  data-test-id="loyalty-card-text-info"
                />
              </TitleAndDescriptionContainer>
              <LoyaltyCardStampProgressMeter
                isOutletList={false}
                loyaltyCard={loyaltyCard}
                canStampBeEarned={canStampBeEarned}
                basketTotals={basketTotals}
                data-test-id="loyalty-card-container-stampprogress"
              />
            </DescriptionAndStampsContainer>
          </LoyaltyCardIconAndTextContainer>
        </LoyaltyCardInnerContainer>
      </LoyaltyCardLinkContainer>
    </OuterContainer>
  )
}
